import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import AuditTimeline from "../components/AuditTimeline";
import { Send, XCircle, ArrowLeft, CheckCircle2, Clock, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  DRAFT: "bg-gray-100 text-gray-700 border-gray-300",
  PENDING_APPROVAL: "bg-yellow-100 text-yellow-800 border-yellow-300",
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  FOR_REVISION: "bg-orange-100 text-orange-800 border-orange-300",
  CANCELLED: "bg-red-100 text-red-800 border-red-300",
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, [id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [oRes, auditRes, appRes] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get(`/admin/audit-logs?entity_type=order&entity_id=${id}&limit=50`).catch(() => ({ data: { items: [] } })),
        api.get(`/approvals?entity_type=order`).catch(() => ({ data: { items: [] } })),
      ]);
      setOrder(oRes.data);
      setAuditLogs(auditRes.data.items || []);
      setApprovals((appRes.data.items || []).filter((a) => a.entity_id === id));
    } catch { toast.error("Failed to load order"); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await api.post(`/orders/${id}/submit`);
      toast.success("Order submitted for approval");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;
  if (!order) return <div className="text-center py-12 text-gray-400">Order not found</div>;

  const o = order;
  const approval = approvals[0];

  return (
    <div data-testid="order-detail-page" className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/orders")} className="rounded-sm">
            <ArrowLeft size={16} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{o.number}</h1>
              <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-sm border ${statusColors[o.status] || ""}`}>
                {o.status?.replace(/_/g, " ")}
              </span>
              <Badge variant="outline" className="rounded-sm text-[10px]">{o.type}</Badge>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">Created {o.created_at ? new Date(o.created_at).toLocaleString() : ""} by {o.creator_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(o.status === "DRAFT" || o.status === "FOR_REVISION") && (
            <Button data-testid="submit-order-detail" size="sm" onClick={handleSubmit} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">
              <Send size={14} className="mr-1" /> Submit
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border border-gray-200 shadow-sm rounded-sm">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              <div><div className="text-[10px] uppercase tracking-wider text-gray-400">Customer</div><div className="text-sm font-medium mt-0.5">{o.customer_name}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-gray-400">Company</div><div className="text-sm font-medium mt-0.5">{o.company_name}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-gray-400">Type</div><div className="text-sm font-medium mt-0.5">{o.type}</div></div>
              {o.quotation_id && <div><div className="text-[10px] uppercase tracking-wider text-gray-400">From Quotation</div><div className="text-sm font-medium mt-0.5 text-[#0052CC]">{o.quotation_id.slice(0, 8)}...</div></div>}
            </div>

            {o.notes && <div className="mb-4 p-3 bg-gray-50 rounded-sm text-sm text-gray-600">{o.notes}</div>}

            <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-3">Line Items</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 border-b">
                  <th className="text-left py-2 px-2">Item Code</th>
                  <th className="text-left py-2 px-2">Item Name</th>
                  <th className="text-left py-2 px-2">Batch</th>
                  <th className="text-right py-2 px-2">Qty</th>
                  <th className="text-right py-2 px-2">Price</th>
                  <th className="text-right py-2 px-2">Disc%</th>
                  <th className="text-right py-2 px-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {o.lines?.map((l) => (
                  <tr key={l.id} className="border-b border-gray-100">
                    <td className="py-2 px-2 font-medium">{l.item_code}</td>
                    <td className="py-2 px-2 text-gray-600">{l.item_name}</td>
                    <td className="py-2 px-2 text-gray-500">{l.batch || "—"}</td>
                    <td className="py-2 px-2 text-right">{l.quantity}</td>
                    <td className="py-2 px-2 text-right">{l.unit_price?.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right">{l.discount_pct}%</td>
                    <td className="py-2 px-2 text-right font-medium">{l.line_total?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end mt-3 pt-3 border-t">
              <span className="text-xs uppercase tracking-wider text-gray-500 mr-4">Grand Total</span>
              <span className="text-xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{o.total_amount?.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {approval && (
            <Card className="border border-gray-200 shadow-sm rounded-sm">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-3">Approval Status</h3>
                <div className="space-y-3">
                  {approval.steps?.map((step) => (
                    <div key={step.id} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${step.status === "APPROVED" ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                        {step.status === "APPROVED" ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-medium">Step {step.step_order}: {step.role_required}</div>
                        <div className="text-[10px] text-gray-500">{step.status} {step.approver_name ? `by ${step.approver_name}` : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border border-gray-200 shadow-sm rounded-sm">
            <CardContent className="p-5">
              <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-3">Activity Timeline</h3>
              <AuditTimeline events={auditLogs} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
